import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import '../../assets/styles/AdminShared.css';

const AdminWebsiteRevision = () => {
  const { projectId } = useParams(); // Get the projectId from the route
  const [projectName, setProjectName] = useState("Loading...");
  const [responses, setResponses] = useState({});
  const [revisionLink, setRevisionLink] = useState(""); // State for the link
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Predefined questions for website revision
  const questions = [
    "Does the homepage look and function as expected? Are all buttons and links working correctly?",
    "Do all secondary pages (e.g., About Us, Contact Us) display correctly and function as expected?",
    "Are all buttons across the website working as intended and directing to the correct pages?",
    "Is the text on each page consistent, clear, and free of typos or errors?",
    "Do all images, videos, and animations load correctly on each page?",
    "Are all forms (e.g., contact forms, sign-up forms) working correctly?",
    "Does the navigation menu work smoothly on desktop and mobile devices?",
    "Is the website mobile-friendly? Do all pages display properly on smaller screens?",
    "Does the overall design of each page align with the brand identity (e.g., colors, fonts, style)?",
    "Do all calls-to-action (e.g., buttons, links) encourage engagement and work as intended?",
    "Are there any parts of the website that confused you or seemed unclear?",
    "What specific changes or improvements would you like to see?",
    "Is there anything else you'd like to add that hasn't been covered?"
  ];

  useEffect(() => {
    const fetchWebsiteRevisionData = async () => {
      try {
        if (!projectId) throw new Error("Project ID is missing.");

        const projectRef = doc(db, "projects", projectId);
        const projectSnap = await getDoc(projectRef);

        if (projectSnap.exists()) {
          const projectData = projectSnap.data();
          setProjectName(projectData.projectName || "Unnamed Project");
          setResponses(projectData.revisionDetails?.websiteRevision?.form || {});
          setRevisionLink(projectData.revisionDetails?.websiteRevision?.link || ""); // Fetch the link
        } else {
          throw new Error("Project not found.");
        }
      } catch (err) {
        console.error("Failed to fetch project data:", err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWebsiteRevisionData();
  }, [projectId]);

  const handleInputChange = (question, value) => {
    setResponses((prevResponses) => ({
      ...prevResponses,
      [question]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, {
        "revisionDetails.websiteRevision.form": responses,
        "revisionDetails.websiteRevision.link": revisionLink, // Save the link
      });

      alert("Responses and link updated successfully!");
    } catch (err) {
      console.error("Failed to update responses:", err.message);
      setError("Failed to update responses: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <a href={`/admin/project/${projectId}`} className="ap-back">← Back to project</a>
        <p className="ap-eyebrow">Admin — Revision</p>
        <h1 className="ap-title">{projectName}: Website Revision</h1>
        <p className="ap-subtitle">View or edit the client's responses for this revision.</p>
      </div>

      {loading ? (
        <div className="ap-loading"><p>Loading...</p></div>
      ) : error ? (
        <div className="ap-loading ap-loading--error"><p>{error}</p></div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="ap-section">
            <div className="ap-section-header">
              <h2 className="ap-section-title">Revision Link</h2>
            </div>
            <div className="ap-section-body">
              <div className="ap-form-group">
                <label>Revision Link</label>
                <input type="text" value={revisionLink} onChange={(e) => setRevisionLink(e.target.value)} placeholder="Enter or update the revision link" />
              </div>
            </div>
          </div>

          <div className="ap-section">
            <div className="ap-section-header">
              <h2 className="ap-section-title">Client Responses</h2>
            </div>
            <div className="ap-section-body">
              {questions.map((question, index) => (
                <div key={index} className="ap-form-group">
                  <label>{question}</label>
                  <textarea value={responses[question] || ""} onChange={(e) => handleInputChange(question, e.target.value)} placeholder="Enter response here..." />
                </div>
              ))}
            </div>
          </div>

          <div className="ap-btn-row" style={{ marginTop: '24px' }}>
            <button type="submit" className="ap-btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminWebsiteRevision;
